/** Mint an import key that grants exactly what this run needs.
 *
 *  Offered when the chosen key cannot write the kinds being synced. That is
 *  worth a dedicated path because the failure it prevents is invisible: a
 *  missing permission does not fail the import, it drops the entity and logs at
 *  debug, so the run reports success and imports nothing.
 *
 *  The key is deliberately narrow — create and update for the selected
 *  catalogue kinds only. The tenant's default key grants all twelve flags
 *  including the three user ones a product sync never writes; widening beyond
 *  what the run needs should be a deliberate act, not a side effect of a
 *  convenience button.
 */
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import * as integrationsApi from '@/api/integrations';
import type { ImportSource } from '@/api/integrations';
import { useSnackbar } from '@/components/Snackbar';
import { importSourcesKey } from '@/features/tour-operators/integrations/config-widgets/hooks';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The new key, so the caller can select it immediately. */
  onCreated: (source: ImportSource) => void;
  tourOperatorId: number;
  tenantName: string;
  /** Catalogue kinds the run will write; the key is granted exactly these. */
  entities: string[];
}

export function CreateImportKeyDialog({
  open,
  onClose,
  onCreated,
  tourOperatorId,
  tenantName,
  entities,
}: Props) {
  const { show } = useSnackbar();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: () =>
      integrationsApi.createImportSource(tourOperatorId, { name: name.trim(), entities }),
    onSuccess: (src) => {
      // The list is cached with a five-minute staleTime, so without this the
      // new key stays invisible: the picker cannot resolve the code it was
      // just handed, and the permissions badge goes blank.
      const key = importSourcesKey(tourOperatorId);
      queryClient.setQueryData<ImportSource[]>(key, (old) => (old ? [...old, src] : [src]));
      // ...and reconcile with the server's own ordering (default key first).
      void queryClient.invalidateQueries({ queryKey: key });
      show(`Import key "${src.name}" created and selected`, 'success');
      onCreated(src);
      setName('');
      onClose();
    },
    onError: (e: Error) => show(e.message || 'Could not create the import key', 'error'),
  });

  const suggested = `${tenantName}: product sync`;
  const trimmed = name.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create an import key</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <DialogContentText>
            The key will be allowed to create and update <strong>{entities.join(', ')}</strong> for{' '}
            {tenantName} — and nothing else.
          </DialogContentText>

          <TextField
            autoFocus
            size="small"
            label="Key name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={suggested}
            helperText="How you will recognise it in the picker. Names must be unique for this operator."
            fullWidth
            slotProps={{ htmlInput: { maxLength: 256 } }}
          />

          <Alert severity="info">
            <Typography variant="body2">
              Deliberately narrower than the operator&apos;s default key, which can also write
              users. A product sync never writes users, so this one cannot either.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!trimmed || create.isPending}
          onClick={() => create.mutate()}
        >
          Create and use
        </Button>
      </DialogActions>
    </Dialog>
  );
}
